import { useState, useEffect } from 'react';
import './OurCampus.css';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import DynamicBanner from '../../Pages/include/DynamicBanner';
import api from '../../../api';

const OurCampus = () => {
  const [activeTab, setActiveTab] = useState('');
  const [activeCampus, setActiveCampus] = useState(''); // Sub-tab active state
  const [activeMobileAccordion, setActiveMobileAccordion] = useState('');
  const [campusData, setCampusData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabItems, setTabItems] = useState([]);
  const [tabDisplayNames, setTabDisplayNames] = useState({});

  // Helper: Format image URLs
  const formatImage = (img) => {
    if (!img) return null;
    return `${process.env.REACT_APP_API_URL}/public/${img.replace(/^\//, "")}`;
  };

  // Helper: Format banner images
  const formatBannerImages = (images) => {
    if (!images || !Array.isArray(images)) return [];
    return images.map(img => 
      img ? `/${img.replace(/^\//, "")}` : null
    ).filter(Boolean);
  };

  // Helper: Process text content
  const processTextContent = (textArray) => {
    if (!textArray || !Array.isArray(textArray)) return [];
    return textArray.flat().filter(item => item && item.trim() !== '');
  };

  // Helper: Render Text Content (Refined Logic)
  const renderTextContent = (textContent) => {
    if (!textContent || textContent.length === 0) return null;

    // Check if ANY item in the array contains HTML tags (like from Summernote)
    const isHtml = textContent.some(text => text.includes('<') && text.includes('>'));

    if (isHtml) {
      // If HTML exists, render it directly
      return textContent.map((html, index) => (
        <div key={index} dangerouslySetInnerHTML={{ __html: html }} className="campus-html-content mb-3" />
      ));
    }

    // If NO HTML tags, check if it's a list (multiple items or manual newlines)
    // Flatten array if items contain newlines
    const plainTextItems = textContent.map(text => 
      text.includes('\r\n') ? text.split('\r\n') : [text]
    ).flat().filter(t => t.trim() !== '');

    // If multiple plain text items, render as a list with icons
    if (plainTextItems.length > 1) {
      return (
        <ul className="campuses-list">
          {plainTextItems.map((item, index) => (
            <li key={index}>
              <i className="fa fa-check-circle-o"></i>
              {item.trim()}
            </li>
          ))}
        </ul>
      );
    } 
    
    // If just one single plain text item, render as paragraph
    return plainTextItems.map((text, index) => <p key={index} className="mb-3">{text}</p>);
  };

  // --- 1. Fetch Data ---
  useEffect(() => {
    const fetchCampusData = async () => {
      try {
        const response = await api.get("/api/sitepages/academic/our-campus");
        
        if (!response.data || !response.data.status) {
          throw new Error("Invalid API response");
        }

        const data = response.data.data;

        // Process Banner
        const banner = {
          desktop_title: data.banner?.desktop_title || 'Campus Life',
          desktop_images: formatBannerImages(data.banner?.desktop_images),
          mobile_title: data.banner?.mobile_title,
          mobile_images: formatBannerImages(data.banner?.mobile_images)
        };

        // Process Content Recursively
        const formattedContent = (data.content || []).map(section => {
          const processedSection = { ...section };

          if (section.sub_tabs) {
            processedSection.sub_tabs = section.sub_tabs.map(subTab => ({
              ...subTab,
              multiple_tab: (subTab.multiple_tab || []).map(tab => ({
                ...tab,
                items: (tab.items || []).map(item => ({
                  ...item,
                  images: (item.images || []).map(formatImage),
                  text_content: processTextContent(item.text_content)
                }))
              }))
            }));
          } else if (section.normal) {
            processedSection.normal = section.normal.map(normalTab => ({
              ...normalTab,
              items: (normalTab.items || []).map(item => ({
                ...item,
                images: (item.images || []).map(formatImage),
                text_content: processTextContent(item.text_content)
              }))
            }));
          }

          return processedSection;
        });

        // Set Tabs
        const tabs = formattedContent.map(s => s.main_heading).filter(Boolean);
        const displayNames = {};
        formattedContent.forEach(s => {
          if (s.main_heading) {
            displayNames[s.main_heading] = s.main_heading;
          }
        });

        // Set Initial Active Tab
        if (tabs.length > 0 && !activeTab) {
          setActiveTab(tabs[0]);
        }

        // Set Initial Active Sub-Tab (Fixed: Defaults to first available sub-tab)
        const firstSection = formattedContent[0];
        if (firstSection?.sub_tabs?.length > 0) {
          setActiveCampus(firstSection.sub_tabs[0].tab_heading.toLowerCase());
        }

        setCampusData({ banner, content: formattedContent });
        setTabItems(tabs);
        setTabDisplayNames(displayNames);
        setIsLoading(false);

      } catch (err) {
        console.error("Error fetching campus data:", err);
        setError("Failed to load campus data");
        setIsLoading(false);
      }
    };

    fetchCampusData();
  }, []);

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    // Auto-select first sub-tab when switching main tabs
    const section = campusData.content.find(s => s.main_heading === tabName);
    if (section?.sub_tabs?.length > 0) {
      setActiveCampus(section.sub_tabs[0].tab_heading.toLowerCase());
    }
  };

  const handleCampusClick = (campusName) => setActiveCampus(campusName);
  const handleAccordionToggle = (id) => setActiveMobileAccordion(activeMobileAccordion === id ? null : id);

  // --- Slider Settings ---
  const getSliderSettings = (slidesToShow = 3) => ({
    dots: false,
    infinite: true,
    speed: 600,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2500,
    responsive: [
      { breakpoint: 992, settings: { slidesToShow: Math.min(2, slidesToShow) } },
      { breakpoint: 576, settings: { slidesToShow: 1 } }
    ]
  });

  // --- Render Layout for Single Item ---
  const renderContentLayout = (item) => {
    const hasText = item.text_content && item.text_content.length > 0;
    const hasImages = item.images && item.images.length > 0;

    // This function ONLY renders the content, NOT the heading
    return (
      <div key={Math.random()} >
        {hasText && hasImages ? (
          <div className="row  d-flex justify-content-center align-items-center">
            <div className="col-md-8 col-12">{renderTextContent(item.text_content)}</div>
            <div className="col-md-4 col-12">
              {item.images.length > 1 ? (
                <div className="smart-campus-slider mt-4">
                  <Slider {...getSliderSettings(1)}>
                    {item.images.map((img, i) => (
                      <div key={i} className="slide-item text-center">
                        <img src={img} alt="slider" style={{ borderRadius: "10px" }} className="img-fluid" />
                      </div>
                    ))}
                  </Slider>
                </div>
              ) : (
                <img src={item.images[0]} alt="content" style={{ borderRadius: "10px" }} className="img-fluid" />
              )}
            </div>
          </div>
        ) : hasText ? (
          <div className="row "><div className="col-12">{renderTextContent(item.text_content)}</div></div>
        ) : hasImages ? (
          <div className="row ">
            <div className="col-12">
              {item.images.length > 1 ? (
                <div className="smart-campus-slider mt-4">
                  <Slider {...getSliderSettings(item.images.length > 2 ? 3 : item.images.length)}>
                    {item.images.map((img, i) => (
                      <div key={i} className="slide-item text-center">
                        <img src={img} alt="slider" className="img-fluid rounded" style={{ maxHeight: "300px", objectFit: "cover" }} />
                      </div>
                    ))}
                  </Slider>
                </div>
              ) : (
                <div className="text-center">
                  <img src={item.images[0]} alt="content" className="img-fluid rounded" style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "contain" }} />
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  // --- Render Section Content (Desktop) ---
  const renderSectionContent = () => {
    if (!campusData) return null;
    const section = campusData.content.find(s => s.main_heading === activeTab);
    if (!section) return <div>Select a section</div>;

    if (section.sub_tabs && section.sub_tabs.length > 0) {
      
      return (
        <div className="tab-pane fade show active">
          <ul className="nav nav-tabs d-flex justify-content-center justify-content-md-start align-items-center mb-4" role="tablist">
            {section.sub_tabs.map((campus) => (
              <li key={campus.tab_heading} className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeCampus === campus.tab_heading.toLowerCase() ? 'active' : ''}`}
                  onClick={() => handleCampusClick(campus.tab_heading.toLowerCase())}
                  type="button"
                >
                  {campus.tab_heading}
                </button>
              </li>
            ))}
          </ul>

          <div className="tab-content">
            {section.sub_tabs.map((campus) => (
              activeCampus === campus.tab_heading.toLowerCase() && (
                <div key={campus.tab_heading} className="tab-pane fade show active">
                  {/* Show campus tab heading as main title */}
                  <div className="sec-title text-center py-2"><h2>{campus.tab_heading}</h2></div>
                  
                  {/* Loop through each multiple_tab and its items */}
                  {(campus.multiple_tab || []).map((tab, tabIndex) => (
                    <div key={tabIndex}>
                      {/* Show tab heading if it exists */}
                      {tab.heading && (
                        <div className="sec-title text-center py-3"><h3>{tab.heading}</h3></div>
                      )}
                      
                      {/* Render each item */}
                      {(tab.items || []).map((item, itemIndex) => (
                        <div key={`${tabIndex}-${itemIndex}`}>
                          {/* Show item heading only if it's NOT similar to campus tab heading */}
                          {item.item_heading && 
                           item.item_heading.trim() !== "" &&
                           !item.item_heading.toLowerCase().includes(campus.tab_heading.toLowerCase()) && 
                           !(campus.tab_heading.toLowerCase() === "kelambakkam" && item.item_heading.toLowerCase().includes("kelambakkam")) &&
                           !(campus.tab_heading.toLowerCase() === "kgf" && (item.item_heading.toLowerCase().includes("kgf") || item.item_heading.toLowerCase().includes("kolar gold fields"))) && (
                            <h4 className="mb-2 mt-2">{item.item_heading}</h4>
                          )}
                          {renderContentLayout(item)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )
            ))}
          </div>
        </div>
      );
    } else if (section.normal) {
      return (
        <div className="tab-pane fade show active">
          {section.normal.map((normalTab, tabIndex) => (
            <div key={tabIndex}>
              {/* Show normal tab heading if it exists */}
              {normalTab.heading && (
                <div className="sec-title text-center py-4"><h2>{normalTab.heading}</h2></div>
              )}
              
              {/* Render each item in normal tab */}
              {(normalTab.items || []).map((item, itemIndex) => (
                <div key={`${tabIndex}-${itemIndex}`}>
                  {/* Show item heading if it exists */}
                  {item.item_heading && item.item_heading.trim() !== "" && (
                    <div className="sec-title text-center py-4"><h2>{item.item_heading}</h2></div>
                  )}
                  {renderContentLayout(item)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // --- Render Mobile Content ---
  const renderMobileContent = () => {
    if (!campusData) return null;
    
    return campusData.content.map((section, index) => {
      const id = `mob-acc-${index}`;
      const isActive = activeMobileAccordion === id;
      
      return (
        <div key={index} className="accordion-item">
          <h5 className="accordion-header">
            <button 
              className={`accordion-button ${isActive ? '' : 'collapsed'}`} 
              type="button" 
              onClick={() => handleAccordionToggle(id)}
            >
              {section.main_heading}
            </button>
          </h5>
          <div className={`accordion-collapse collapse ${isActive ? 'show' : ''}`}>
            <div className="accordion-body">
              {section.sub_tabs ? (
                <div className="tab-content">
                  <ul className="nav nav-tabs d-flex justify-content-center mb-3">
                    {section.sub_tabs.map(c => (
                      <li key={c.tab_heading} className="nav-item">
                        <button 
                          className={`nav-link ${activeCampus === c.tab_heading.toLowerCase() ? 'active' : ''}`} 
                          onClick={() => handleCampusClick(c.tab_heading.toLowerCase())}
                        >
                          {c.tab_heading}
                        </button>
                      </li>
                    ))}
                  </ul>
                  
                  {section.sub_tabs.map(c => 
                    activeCampus === c.tab_heading.toLowerCase() && (
                      <div key={c.tab_heading}>
                        {/* Show campus tab heading for mobile */}
                        <div className="sec-title text-center py-3"><h3>{c.tab_heading}</h3></div>
                        
                        {/* Render items */}
                        {c.multiple_tab.map((tab, tabIndex) => (
                          <div key={tabIndex}>
                            {tab.heading && (
                              <div className="sec-title text-center py-2"><h4>{tab.heading}</h4></div>
                            )}
                            
                            {tab.items.map((item, itemIndex) => (
                              <div key={`${tabIndex}-${itemIndex}`}>
                                {/* Show item heading only if it's NOT similar to campus tab heading */}
                                {item.item_heading && 
                                 item.item_heading.trim() !== "" &&
                                 !item.item_heading.toLowerCase().includes(c.tab_heading.toLowerCase()) && 
                                 !(c.tab_heading.toLowerCase() === "kelambakkam" && item.item_heading.toLowerCase().includes("kelambakkam")) &&
                                 !(c.tab_heading.toLowerCase() === "kgf" && (item.item_heading.toLowerCase().includes("kgf") || item.item_heading.toLowerCase().includes("kolar gold fields"))) && (
                                  <h5 className="mb-3 mt-3">{item.item_heading}</h5>
                                )}
                                {renderContentLayout(item)}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              ) : section.normal ? (
                section.normal.map((normalTab, tabIndex) => (
                  <div key={tabIndex}>
                    {normalTab.heading && (
                      <div className="sec-title text-center py-3"><h3>{normalTab.heading}</h3></div>
                    )}
                    
                    {normalTab.items.map((item, itemIndex) => (
                      <div key={`${tabIndex}-${itemIndex}`}>
                        {item.item_heading && item.item_heading.trim() !== "" && (
                          <div className="sec-title text-center py-3"><h3>{item.item_heading}</h3></div>
                        )}
                        {renderContentLayout(item)}
                      </div>
                    ))}
                  </div>
                ))
              ) : null}
            </div>
          </div>
        </div>
      );
    });
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!campusData) return null;

  return (
    <>
      <DynamicBanner
        title={campusData.banner.desktop_title}
        bgImage={campusData.banner.desktop_images}
        bgImageMobile={campusData.banner.mobile_images}
      />

      <section className="pt_80 pb_80 our-campus-section" style={{ textAlign: 'justify' }}>
        <div className="container">
          <div className="row">
            
            {/* Desktop Sidebar */}
            <div className="col-md-3 d-none d-md-block">
              <div className="campuses-section">
                <nav className="nav flex-column">
                  {tabItems.map((item) => (
                    <button
                      key={item}
                      className={`nav-link ${activeTab === item ? 'active' : ''}`}
                      onClick={() => handleTabClick(item)}
                      type="button"
                    >
                      {tabDisplayNames[item]}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Desktop Content */}
            <div className="col-md-9 d-none d-md-block">
              <div className="tab-content">
                {renderSectionContent()}
              </div>
            </div>

            {/* Mobile Accordion */}
            <div className="col-12 d-md-none">
              <div className="accordion" id="mobileAccordioncampus">
                {renderMobileContent()}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default OurCampus;